import React, { useState } from 'react';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { USER_ROLE_ADMIN } from 'common/userRoles';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  makeListingAddressStreetDisplay,
} from 'common/listingHelpers';

import {
  MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED,
  MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
  MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
  MARKETING_PARTNER_LISTING_UPDATE_STATUS_MARK_AS_RESOLVED,
  MARKETING_PARTNER_LISTING_UPDATE_STATUS_BRING_BACK_ON_MARKET,
} from 'common/marketingPartnerListingConstants';

import {
  LISTING_WALKTHROUGH_CONDITION_ITEMS,
  LISTING_WALKTHROUGH_CONDITION_OTHER,
} from 'common/listingConstants';

import useDispatch from 'client/hooks/useDispatch';

import SrButton, { SR_BUTTON_THEME_ADOBE_YELLOW } from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrViewPhotosCard from 'client/components/SrViewPhotosCard/SrViewPhotosCard';
import SrFlex from 'client/components/SrFlex';
import SrIcon from 'client/components/SrIcon';
import SrSeparator from 'client/components/SrSeparator';
import SrSetupHealth from 'client/components/SrSetupHealth';
import SrMonitorErrorCard from 'client/components/SrMonitorErrorCard/SrMonitorErrorCard';
import SrTag, {
  SR_TAG_THEME_LIGHTBLUE_BLACK,
  SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER,
  SR_TAG_THEME_WHITE_BLACK,
} from 'client/components/SrTag/SrTag';
import SrAdminListingNotes from 'client/components/SrAdminListingNotes';
import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';

import './SrGoingToMarketCard.less';

const formatPartnerListingDate = (date) => {
  if (!date) {
    return '';
  }

  return momentWithAustinTimezone(date).format('MMM D [at] h:mma');
};

const MARKETING_PARTNER_LISTING_STATUS_TO_LABEL_GETTERS = {
  [MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS]: (marketingPartnerListing) => {
    const displayPhotos = marketingPartnerListing.walkthroughPhotoUrls.length > 0;
    const walkthroughNotes = LISTING_WALKTHROUGH_CONDITION_ITEMS
      .filter(({ value }) => (
        (marketingPartnerListing.walkthroughConditions || []).includes(value)
        && value !== LISTING_WALKTHROUGH_CONDITION_OTHER
      ))
      .concat([{ label: marketingPartnerListing.walkthroughExtraCondition || '' }])
      .concat([{ label: marketingPartnerListing.walkthroughNotes || '' }])
      .filter(({ label }) => label)
      .map(({ label }) => (
        <div key={label}>
          {`“${label.replace('No: ', '')}”`}
        </div>
      ));

    return typeof marketingPartnerListing.walkthroughIssuesResolved === 'boolean'
      && marketingPartnerListing.walkthroughIssuesResolved ? (
        <SrText color='grey600' className='resolvedRow'>
          {
            'Resolved ' +
          momentWithAustinTimezone(
            marketingPartnerListing.walkthroughIssuesResolvedDate
          ).format('MMM D [at] hA')
          }
        </SrText>
      ) : (
        <>
          <SrLabel
            color='red'
            backgroundColor='rgba(232, 0, 29, 0.08)'
            label='Not ready to market'
            iconUrl='/images/caution-icon-red.svg'
            iconDropShadow='0 3px 4px rgba(232, 0, 29, 0.27)'
          />
          {walkthroughNotes.length > 0
            && (
              <>
                <SrBox marginTop='16px'>
                  <SrText
                    font='greycliff'
                    weight='800'
                    fontSize='18px'
                    lineHeight='26px'
                    letterSpacing='0.5px'
                    color='greyDark'
                  >
                    {marketingPartnerListing.updatedDate ? `Notes on
                  ${formatPartnerListingDate(marketingPartnerListing.updatedDate)}`
                      : 'Notes from the Sunroom team:'}
                  </SrText>
                </SrBox>
                <SrBox marginTop='4px' marginBottom='22px'>
                  <SrText
                    font='greycliff'
                    weight='400'
                    fontSize='16px'
                    lineHeight='26px'
                    color='blueGreycliff'
                  >
                    {walkthroughNotes}
                  </SrText>
                </SrBox>
              </>
            )}
          {displayPhotos && (
            <SrBox marginTop={!walkthroughNotes.length > 0 ? '16px' : null}>
              <SrViewPhotosCard photos={marketingPartnerListing.walkthroughPhotoUrls} />
            </SrBox>
          )}
        </>
      );
  },
  [MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET]: (marketingPartnerListing) => {
    return (
      <>
        <SrLabel
          color='orange300'
          backgroundColor='rgba(231, 113, 27, 0.08)'
          label='Temporarily Off-Market'
          iconUrl='/images/clock-icon-orange.svg'
          iconDropShadow='0 3px 4px rgba(231, 113, 27, 0.27)'
        />
        <span>
          {(marketingPartnerListing.temporarilyOffMarketReason
            || marketingPartnerListing.temporarilyOffMarketReasonProvided)
            ? (
              <>
                <SrBox marginTop='16px'>
                  <SrText
                    font='greycliff'
                    weight='800'
                    fontSize='18px'
                    lineHeight='26px'
                    letterSpacing='0.5px'
                    color='greyDark'
                  >
                    Reason
                    {marketingPartnerListing.updatedDate &&
                      ` from ${formatPartnerListingDate(marketingPartnerListing.updatedDate)}`}
                    {marketingPartnerListing.updatedByUser && ` by
                  ${marketingPartnerListing.updatedByUser.name}`}
                  </SrText>
                </SrBox>
                <SrBox marginTop='4px' marginBottom='22px'>
                  <SrText
                    font='greycliff'
                    weight='400'
                    fontSize='16px'
                    lineHeight='26px'
                    color='blueGreycliff'
                  >
                    {`“${marketingPartnerListing.temporarilyOffMarketReasonProvided
                      || marketingPartnerListing.temporarilyOffMarketReason}"`}
                  </SrText>
                </SrBox>
              </>
            )
            : (
              <SrBox marginTop='16px' marginBottom='16px'>
                <SrText
                  font='greycliff'
                  weight='800'
                  fontSize='18px'
                  lineHeight='26px'
                  letterSpacing='0.5px'
                  color='greyDark'
                >
                  Taken temporarily off-market
                  {marketingPartnerListing.updatedByUser && ` by
                  ${marketingPartnerListing.updatedByUser.name}`}
                  {marketingPartnerListing.updatedDate && ` on
                  ${formatPartnerListingDate(marketingPartnerListing.updatedDate)}`}
                </SrText>
              </SrBox>
            )}
        </span>
      </>
    );
  },
};

MARKETING_PARTNER_LISTING_STATUS_TO_LABEL_GETTERS[MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED] = (
  MARKETING_PARTNER_LISTING_STATUS_TO_LABEL_GETTERS[MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS]
);

const SrGoingToMarketCard = ({
  marketingPartnerListing,
  onEdit,
  partnerSlug,
  maxWidth,
  currentUser,
  className,
  reloadData,
}) => {
  const dispatch = useDispatch();
  const [listingNotesModal, setListingNotesModal] = useState(null);

  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const _shouldDisplaySetupHealth = () => {
    return marketingPartnerListing.setupHealth
      && isAdmin;
  };

  const _getSetupHealthComponent = () => {
    const shouldDisplaySetupHealth = _shouldDisplaySetupHealth();

    if (!shouldDisplaySetupHealth) {
      return null;
    }

    if (marketingPartnerListing.setupHealth?.error) {
      return (
        <SrMonitorErrorCard
          monitorName='Setup Health'
          noCard
        />
      );
    }

    return (
      <SrSetupHealth
        setupHealth={marketingPartnerListing.setupHealth}
        listingId={marketingPartnerListing.listingId}
        requirePhotos={marketingPartnerListing.requirePhotos}
        photographyArrivedDate={marketingPartnerListing.photographyJob?.photographyArrivedDate}
        photoLiveDate={marketingPartnerListing.photoLiveDate}
        photoLiveCompletedDate={marketingPartnerListing.photoLiveCompletedDate}
        firstInReviewDate={marketingPartnerListing.firstInReviewDate}
        firstActiveDate={marketingPartnerListing.listingActiveDate}
      />
    );
  };

  const isAdmin = currentUser.roles.includes(USER_ROLE_ADMIN);
  const SrCardOrSrBox = isAdmin ? SrLink : SrBox;

  const statusLabelGetter = MARKETING_PARTNER_LISTING_STATUS_TO_LABEL_GETTERS[marketingPartnerListing.status];
  const statusLabel = statusLabelGetter ? statusLabelGetter(marketingPartnerListing) : '';
  const shouldDisplaySetupHealth = _shouldDisplaySetupHealth();
  const setupHealthComponent = _getSetupHealthComponent();
  const onNoteClick = (event, listing) => {
    event.preventDefault();

    setListingNotesModal(listing);
  };
  const getNotesTagComponent = () => {
    const hasNotes = marketingPartnerListing.notesCount > 0;
    const blueTheme = isAdmin ? SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER : SR_TAG_THEME_LIGHTBLUE_BLACK;
    const theme = hasNotes
      ? blueTheme
      : SR_TAG_THEME_WHITE_BLACK;
    const onTagClick = hasNotes && isAdmin
      ? (e) => onNoteClick(e, marketingPartnerListing.listing)
      : undefined;

    return (
      <SrTag
        theme={theme}
        variation='goingToMarketCard'
        onClick={onTagClick}
      >
        {`${pluralize('Note', marketingPartnerListing.notesCount, true)}`}
      </SrTag>
    );
  };
  const onCloseNotesModal = () => {
    if (reloadData) {
      reloadData(marketingPartnerListing);
    }

    setListingNotesModal(null);
  };

  return (
    <div>
      <SrBox
        className={className}
        backgroundColor='white'
        borderRadius={isSmallScreen ? '14px' : '24px'}
        padding='0 0 27px 0'
        styleName='SrGoingToMarketCard'
        maxWidth={maxWidth}
      >
        {marketingPartnerListing.propertyManager && (
          <SrFlex bgColor='orange200' align='center' justify='between' padding='15px 20px'>
            <SrFlex align='center'>
              <SrIcon className='person-filled-orange' marginRight={8} />
              <SrText weight='bold' color='orange300'>
                {marketingPartnerListing.propertyManager.name}
              </SrText>
            </SrFlex>
          </SrFlex>
        )}
        <SrBox padding='23px 19px 0'>
          <SrFlex justify='between'>
            <SrCardOrSrBox to={`/admin/listings/edit/${marketingPartnerListing.listingId}`} target='_blank'>
              {isAdmin && marketingPartnerListing.listing?.status === 'Needs Review' && (
                <SrText
                  font='greycliff'
                  color='red'
                  size='14'
                  display='block'
                  weight='800'
                >
                  NEEDS REVIEW
                </SrText>
              )}
              {isAdmin && marketingPartnerListing.partner && (
                <SrText
                  font='greycliff'
                  size='16'
                  color='blue300'
                  display='block'
                >
                  {marketingPartnerListing.partner.displayName}
                </SrText>
              )}
              <SrText
                font='greycliff'
                fontSize='21px'
                weight='800'
              >
                {makeListingAddressStreetDisplay(marketingPartnerListing.listing)}
              </SrText>
              {isAdmin && marketingPartnerListing.marketDisplayName && (
                <SrText
                  font='greycliff'
                  color='grey'
                  size='12'
                  display='block'
                >
                  {marketingPartnerListing.marketDisplayName}
                </SrText>
              )}
            </SrCardOrSrBox>
            <SrText
              font='greycliff'
              weight='800'
              color='blue300'
              cursor='pointer'
              onClick={onEdit}
            >
              Edit
            </SrText>
          </SrFlex>
          <SrBox marginTop='8px' />
          <SrText
            font='greycliff'
            color='blueGreycliff'
            opacity='0.5'
            weight='medium'
          >
            {`Submitted ${formatPartnerListingDate(marketingPartnerListing.createdDate)}`}
          </SrText>
          <SrFlex justify='between' marginTop='8px'>
            {getNotesTagComponent()}
          </SrFlex>
        </SrBox>
        <SrSeparator marginTop='20px' marginBottom='10px' />
        <SrBox padding='0 19px'>
          <SrBox marginBottom='20px' />
          <span>
            {statusLabel}
          </span>
          <SrBox marginTop='27px'>
            {marketingPartnerListing.status === MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS
              ? (
                <SrLink
                  // eslint-disable-next-line max-len
                  to={`/partner/${partnerSlug}/marketing-listing/${marketingPartnerListing.id}/${MARKETING_PARTNER_LISTING_UPDATE_STATUS_MARK_AS_RESOLVED}`}
                >
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                    margin='29px 0 11px 0'
                    hasBoxShadow
                  >
                    Mark As Resolved
                  </SrButton>
                </SrLink>
              )
              : (
                <SrLink
                  // eslint-disable-next-line max-len
                  to={`/partner/${partnerSlug}/marketing-listing/${marketingPartnerListing.id}/${MARKETING_PARTNER_LISTING_UPDATE_STATUS_BRING_BACK_ON_MARKET}`}
                >
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                    margin='29px 0 11px 0'
                    hasBoxShadow
                  >
                    Bring Back On Market
                  </SrButton>
                </SrLink>
              )}
          </SrBox>
        </SrBox>
        {shouldDisplaySetupHealth && (
          <>
            <SrSeparator marginTop='20px' marginBottom='10px' />
            <SrBox
              marginTop='15px'
              padding='0 15px'
            >
              {setupHealthComponent}
            </SrBox>
          </>
        )}
      </SrBox>
      {listingNotesModal && (
        <SrModalV2
          closeOnBackgroundClick
          showCloseButton={false}
          contentMaxWidth='546px'
          contentPadding='0px'
          theme={MODAL_THEME_STACK}
          onClose={onCloseNotesModal}
        >
          <SrCard
            header='Notes (Admin-Only)'
            color={SR_CARD_BULLET_COLOR_BLUE}
            maxWidth='none'
          >
            <SrAdminListingNotes
              listingId={listingNotesModal.id}
              dispatch={dispatch}
              currentUser={currentUser}
            />
          </SrCard>
        </SrModalV2>
      )}
    </div>
  );
};

SrGoingToMarketCard.propTypes = {
  className: PropTypes.string,
  maxWidth: PropTypes.string,
  partnerSlug: PropTypes.string.isRequired,
  marketingPartnerListing: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  reloadData: PropTypes.func,
};

SrGoingToMarketCard.displayName = 'SrGoingToMarketCard';

export default SrGoingToMarketCard;
